import { registerBrick, File as OFile, Transaction, CloudObject } from 'olympe';
import { ReactBrick } from '@olympeio/core';
import React from 'react';
import { combineLatest } from 'rxjs';

export default class DrawioRenderer extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([$.observe('Hidden'), $.observe('Diagram XML'), $.observe('Editable')]);
    }

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @return {(properties?: Object) => React.ReactElement}
     */

    static getReactComponent($) {
        return (props) => {
            const [hidden, diagramXmlFile, editable] = props.values;
            const [diagramXml, setDiagramXml] = React.useState('');
            const [fileName, setFileName] = React.useState('file.drawio');
            const containerRef = React.useRef(null);

            // Read Ofile and convert to xml
            React.useEffect(() => {
                if (!diagramXmlFile) return;

                const fileObj = diagramXmlFile.toObject(true);
                diagramXmlFile
                    .getContentAsString()
                    .then((content) => {
                        let xml;
                        try {
                            xml = atob(content);
                            if (!xml.startsWith('<mxfile')) throw new Error();
                        } catch {
                            xml = content;
                        }
                        setFileName(fileObj['file name']);
                        setDiagramXml(xml);
                    })
                    .catch((error) => {
                        console.error('Failed to load diagram XML:', error);
                        setDiagramXml('');
                    });
            }, [diagramXmlFile]);

            // Start iframe for editable diagram
            React.useEffect(() => {
                if (!containerRef.current) return;

                const iframe = document.createElement('iframe');
                iframe.setAttribute('frameborder', '0');
                iframe.setAttribute('width', '100%');
                iframe.setAttribute('height', '100%');
                iframe.setAttribute('srcdoc', diagramXml);
                containerRef.current.innerHTML = '';
                containerRef.current.appendChild(iframe);
            }, [diagramXml]);

            // Start viewer diagrams script
            React.useEffect(() => {
                const script = document.createElement('script');
                script.src = 'https://viewer.diagrams.net/js/viewer.min.js';
                script.async = true;
                document.body.appendChild(script);
                return () => {
                    document.body.removeChild(script);
                };
            }, []);

            React.useEffect(() => {
                // Wait a bit to allow viewer script to load before processing elements
                const timer = setTimeout(() => {
                    window.GraphViewer?.processElements();
                }, 100);

                return () => clearTimeout(timer);
            }, [diagramXml]);

            React.useEffect(() => {
                const handleMessage = (event) => {
                    if (event.data === 'ready') {
                        const iframe = document.getElementById('embed-diagram');
                        if (iframe && iframe.contentWindow) {
                            iframe.contentWindow.postMessage(diagramXml, '*');
                        }
                        // When the event data starts with <mxfile it means the save event was triggered
                    } else if (typeof event.data === 'string' && event.data.startsWith('<mxfile')) {
                        const transaction = new Transaction(false);
                        const file = transaction.create(OFile);
                        OFile.setContent(transaction, file, fileName, event.data, 'text/xml');
                        transaction
                            .execute()
                            .then(() => {
                                const onSave = $.get('On Save');
                                if (onSave) {
                                    const onSave$ = $.runner(onSave);
                                    const [ctrlFlowInput, fileInput] = onSave.getInputs();
                                    onSave$.set(fileInput, CloudObject.get(file)).trigger(ctrlFlowInput);
                                }
                            })
                            .catch((message) => {
                                throw new Error(message);
                            });
                    }
                };

                window.addEventListener('message', handleMessage);
                return () => window.removeEventListener('message', handleMessage);
            }, [diagramXml]);

            return (
                !hidden && (
                    <div style={{ width: '100%', height: '100%' }}>
                        {editable ? (
                            <iframe
                                id="embed-diagram"
                                src="https://embed.diagrams.net/?embed=1&ui=atlas&spin=1&saveAndExit=1&noSaveBtn=0&noExitBtn=1&nav=1&toolbar=0&edit=1"
                                width="100%"
                                height="600px"
                                title="Draw.io Editor"
                            />
                        ) : (
                            <div
                                ref={containerRef}
                                className="mxgraph"
                                data-mxgraph={JSON.stringify({
                                    highlight: '#0000ff',
                                    nav: true,
                                    resize: true,
                                    xml: diagramXml,
                                })}
                            />
                        )}
                    </div>
                )
            );
        };
    }
}

registerBrick('0199ec1db60790055b29', DrawioRenderer);
